import { LOGOUT } from '@comeon/actions-public';
import { validateSession } from '~/components/authentication/ducks/index.js';
import { requestApi } from '../../../services/api.js';
import {
	REDUCER_NAME,
	ACTION_PREFIX,
	HIGHLIGHT_GAMES_BASE_URL,
	BET_HISTORY_PAGE_SIZE,
	RESPONSE_STATUS_UNAUTHORIZED
} from '../highlight-games-constants.js';
import { extendBetsWithTaxedAmountsAndPossibleWin } from '../highlight-games-utils.js';
import {
	selectBetAmountTaxationRule,
	selectHistoryPollingTime,
	selectWinOverLimitTaxationRule
} from './highlight-games-configuration-duck.js';

// Actions
const FETCH_HISTORY_BETS_REQUESTED = `${ACTION_PREFIX}/FETCH_HISTORY_BETS_REQUESTED`;
const FETCH_HISTORY_BETS_SUCCEEDED = `${ACTION_PREFIX}/FETCH_HISTORY_BETS_SUCCEEDED`;
const FETCH_HISTORY_BETS_FAILED = `${ACTION_PREFIX}/FETCH_HISTORY_BETS_FAILED`;

const SUBSCRIBED_TO_HISTORY = `${ACTION_PREFIX}/SUBSCRIBED_TO_HISTORY`;
const UNSUBSCRIBED_TO_HISTORY = `${ACTION_PREFIX}/UNSUBSCRIBED_TO_HISTORY`;

const POLL_HISTORY_BETS_REQUESTED = `${ACTION_PREFIX}/POLL_HISTORY_BETS_REQUESTED`;
const POLL_HISTORY_BETS_SUCCEEDED = `${ACTION_PREFIX}/POLL_HISTORY_BETS_SUCCEEDED`;
const POLL_HISTORY_BETS_FAILED = `${ACTION_PREFIX}/POLL_HISTORY_BETS_FAILED`;

// Selectors
export const selectHistoryIsLoading = state => state[REDUCER_NAME].history.historyIsLoading;
export const selectHistoryBets = state => state[REDUCER_NAME].history.historyBets;
export const selectHistoryPage = state => state[REDUCER_NAME].history.historyPage;
export const selectTotalHistoryPages = state => state[REDUCER_NAME].history.totalHistoryPages;
export const selectIsPollingHistory = state => state[REDUCER_NAME].history.isPollingHistory;

// Action creators

const buildNextHistoryBets = (newBets, oldBets, state) => {
	let newBetsCopy = extendBetsWithTaxedAmountsAndPossibleWin(
		newBets,
		selectBetAmountTaxationRule(state),
		selectWinOverLimitTaxationRule(state)
	);

	const topPageOfHistoryBets = oldBets.slice(0, newBets.length);

	const newTopOfHistoryBets = topPageOfHistoryBets.map(oldBet => {
		if (newBets.some(newBet => newBet.reference === oldBet.reference)) {
			const updatedBet = newBetsCopy.find(newBet => newBet.reference === oldBet.reference);
			newBetsCopy = newBetsCopy.filter(newBet => newBet.reference !== oldBet.reference);
			return updatedBet;
		}
		return oldBet;
	});

	const newBetHistory = newTopOfHistoryBets.concat(oldBets.slice(newTopOfHistoryBets.length));

	return newBetsCopy.concat(newBetHistory);
};

const pollHistory = () => async (dispatch, getState) => {
	const isPollingHistory = selectIsPollingHistory(getState());
	if (!isPollingHistory) {
		return;
	}

	dispatch({ type: POLL_HISTORY_BETS_REQUESTED });
	try {
		const response = await requestApi(`${HIGHLIGHT_GAMES_BASE_URL}/bets?page=0&size=${BET_HISTORY_PAGE_SIZE}`);
		if (response.error) {
			if (response.status === RESPONSE_STATUS_UNAUTHORIZED) {
				// Double check if we are logged in since we probably are not
				// Note: after this call we expect to be navigated back to hg lobby if we are not logged in
				dispatch(validateSession());
			}
			dispatch({ type: POLL_HISTORY_BETS_FAILED });
		} else {
			const historyBets = selectHistoryBets(getState());
			dispatch({
				type: POLL_HISTORY_BETS_SUCCEEDED,
				payload: {
					bets: buildNextHistoryBets(response.list, historyBets, getState()),
					totalPages: response.totalPages
				}
			});
		}
	} catch (error) {
		dispatch({ type: POLL_HISTORY_BETS_FAILED });
	}

	const historyPollingTime = selectHistoryPollingTime(getState());
	setTimeout(() => dispatch(pollHistory()), historyPollingTime);
};

export const subscribeToHistoryUpdates = () => dispatch => {
	dispatch({ type: SUBSCRIBED_TO_HISTORY });
	dispatch(pollHistory());
};

export const unsubscribeToHistoryUpdates = () => dispatch => {
	dispatch({ type: UNSUBSCRIBED_TO_HISTORY });
};

export const fetchHistoryBets =
	(page = 0) =>
	async (dispatch, getState) => {
		try {
			const response = await requestApi(
				`${HIGHLIGHT_GAMES_BASE_URL}/bets?page=${page}&size=${BET_HISTORY_PAGE_SIZE}`
			);
			if (response.error) {
				dispatch({ type: FETCH_HISTORY_BETS_FAILED });
			} else {
				const betsWithTaxAmounts = extendBetsWithTaxedAmountsAndPossibleWin(
					response.list,
					selectBetAmountTaxationRule(getState()),
					selectWinOverLimitTaxationRule(getState())
				);

				dispatch({
					type: FETCH_HISTORY_BETS_SUCCEEDED,
					payload: {
						bets: betsWithTaxAmounts,
						page,
						totalPages: response.totalPages
					}
				});
			}
		} catch (error) {
			dispatch({ type: FETCH_HISTORY_BETS_FAILED });
			throw new Error(error);
		}
	};

// Reducer
const initialState = {
	historyIsLoading: false,
	historyBets: [],
	historyPage: 0,
	totalHistoryPages: undefined,
	isPollingHistory: false
};

export default (state = initialState, { type, payload }) => {
	switch (type) {
		case FETCH_HISTORY_BETS_REQUESTED:
			return {
				...state,
				historyIsLoading: true
			};
		case FETCH_HISTORY_BETS_SUCCEEDED:
			return {
				...state,
				historyBets: payload.page > state.historyPage ? [...state.historyBets, ...payload.bets] : payload.bets,
				historyPage: payload.page,
				historyIsLoading: false,
				totalHistoryPages: payload.totalPages
			};
		case FETCH_HISTORY_BETS_FAILED:
			return {
				...state,
				historyIsLoading: false
			};
		case SUBSCRIBED_TO_HISTORY:
			return {
				...state,
				isPollingHistory: true
			};
		case UNSUBSCRIBED_TO_HISTORY:
			return {
				...state,
				isPollingHistory: false
			};
		case POLL_HISTORY_BETS_SUCCEEDED:
			return {
				...state,
				historyBets: payload.bets,
				totalHistoryPages: payload.totalPages
			};
		case LOGOUT:
			return initialState;
		default:
			return state;
	}
};
